// Angular
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
// PrimeNG
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
// Directives
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
// Models
import { ControlsOf } from '@shared/models-v3/controls-of.type';
// Components
import { DocumentIdInputComponent } from '@shared/components-v3/atomic/document-id-input/document-id-input.component';
import { PhoneInputComponent } from '@shared/components-v3/atomic/phone-input/phone-input.component';
import { EmailInputComponent } from '@shared/components-v3/atomic/email-input/email-input.component';
import { NameInputComponent } from '@shared/components-v3/atomic/name-input/name-input.component';

import { SessionService } from '@core/services-v2/session/session.service';
import { LoginModalComponent } from './components/login-modal/login-modal.component';
import { LoginHeaderModalComponent } from './components/login-header-modal/login-header-modal.component';
import { environment } from '@env/environment';

import { IEcommerceUser } from '@core/models-v2/auth/user.interface';
import { IGuest } from '@core/models-v2/storage/guest.interface';
import { SessionStorageService } from '@core/storage/session-storage.service';
import { GuestStorageService } from '@core/storage/guest-storage.service';
import { CartApiService } from '@core/services-v2/cart/cart-api/cart-api.service';
import { CustomerService } from '@core/services-v2/customer.service';
import { CommonModule } from '@angular/common';
import { CheckoutStorageService } from '../../services/checkout-storage/checkout-storage.service';

const NG_MODULES = [ReactiveFormsModule, CommonModule];
const PRIME_MODULES = [InputTextModule, PasswordModule, DynamicDialogModule];
const COMPONENTS = [
  DocumentIdInputComponent,
  PhoneInputComponent,
  EmailInputComponent,
  NameInputComponent,
];
const DIRECTIVES = [FormSubmitDirective, ControlErrorsDirective];

@Component({
  selector: 'app-contact-step',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS, DIRECTIVES],
  templateUrl: './contact-step.component.html',
  host: {
    class: 'w-full',
    ngSkipHydration: 'true',
  },
  providers: [DialogService, MessageService],
})
export class ContactStepComponent {
  //#region ViewChild elements
  @ViewChild('contactFormElement')
  contactFormElement!: ElementRef<HTMLFormElement>;
  //#endregion

  /**
   * Limpia la información de contacto del formulario al cerrar la ventana.
   */
  @HostListener('window:beforeunload', ['event'])
  clearCacheOnClose(): void {
    sessionStorage.removeItem('ContactFormSession');
  }

  contactForm!: FormGroup<ControlsOf<IEcommerceUser>>;

  isLoggedIn = signal<boolean>(false);
  guest!: IGuest;
  phoneSelected!: string;
  countryCode = environment.country;
  documentIdLabel = environment.documentId.personalLabel;
  documentIdSample = environment.documentId.personalSample;

  get emailField() {
    return this.contactForm.get('email');
  }

  //region Dependencies
  private readonly sessionService = inject(SessionService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly cartApiService = inject(CartApiService);
  private readonly fb = inject(NonNullableFormBuilder);
  public readonly dialogService = inject(DialogService);
  private readonly guestStorageService = inject(GuestStorageService);
  private readonly retrieveData = inject(CustomerService);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  //endregion

  constructor() {
    this.isLoggedIn.set(this.sessionService.isLoggedIn());
    this.buildForm();
  }

  // REVISAR...
  private buildForm(): void {
    this.contactForm = this.fb.group<ControlsOf<IEcommerceUser>>({
      active: this.fb.control(true),
      avatar: this.fb.control(''),
      // No es necesario esto....
      documentId: this.fb.control('', Validators.required),
      company: this.fb.control(''),
      country: this.fb.control(this.countryCode),
      firstName: this.fb.control('', Validators.required),
      lastName: this.fb.control('', Validators.required),
      loginTemp: this.fb.control(true),
      phone: this.fb.control('', Validators.required),
      email: this.fb.control('', Validators.required),
      userRole: this.fb.control('temp'),
    });
    //valida el formulario cuando se completa y hace el registro
    /*this.contactForm.statusChanges.subscribe((status) => {
      if (status === 'VALID') {
        this.onSubmit();
      }
    });*/
  }

  ngOnInit(): void {
    const guest = this.guestStorageService.get();
    if (guest) this.setFormValues(guest);
  }

  private setFormValues(guest: IGuest): void {
    this.contactForm.patchValue({
      documentId: guest.documentId,
      firstName: guest.firstName,
      lastName: guest.lastName,
      phone: guest.phone,
      email: guest.email,
    });
  }

  /**
   * Actualiza invitado en la base de datos y lo guarda en el storage.
   * @param inputEmail
   * @returns
   */
  /*async registerUser(): Promise<void> {
    const email = '';

    try {
      const resp = await firstValueFrom(this.authService.checkEmail(email));
      if (!resp.exists || (resp.exists && resp.userType === 2)) {
        const session = this.sessionStorage.get();
        const contact = this.contactForm.getRawValue();
        const guestUser = this.setGuestUser(contact);

        // Si tiene session actualiza el contacto.
        if (session) {
          const guest: IGuest = {
            documentId: guestUser.documentId,
            firstName: this.contactForm.get('firstName')?.value || '',
            lastName: guestUser.lastName,
            phone: guestUser.phone,
            email: guestUser.email,
            street: '',
            number: '',
            commune: '',
          };
          try {
            //console.log('probando llamada api:', guest);
            await firstValueFrom(
              this.cartApiService.updateGuest(session.email, guest)
            );
          } catch (error) {
            console.error(error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Ocurrió un error al intentar guardar los datos',
            });
            return;
          }
        }
        let guestSave: IGuest = {
          documentId: guestUser.documentId,
          firstName: guestUser.firstName,
          lastName: guestUser.lastName,
          phone: guestUser.phone,
          email: guestUser.email,
          street: '',
          number: '',
          commune: '',
        };
        this.guestStorageService.set(guestSave);
        //this.returnLoginEvent.emit(guestUser);
        if (this.countryCode === 'cl') {
          this.customerSelectedAddressStorageService.remove();
        }
      } else if (resp.exists && resp.userType != 2) {
        // Ya se encuentra registrado..
      }
    } catch (error) {
      console.error(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ocurrió un error al intentar guardar los datos',
      });
    }
  }*/

  // REVISAR...
  /*onSubmit(): void {
    //se hace el registro con el correo
    this.registerUser(this.email);
    //aqui paso la informacion del formulario al servicio para que se pueda usar en el sidebar
    this.retrieveData.setFormData(this.contactForm.value);
  }*/

  // REVISAR...
  /*private setGuestUser(form: any): IEcommerceUser {
    return {
      active: true,
      avatar: '',
      documentId: form.documentId,
      company: form.firstName + ' ' + form.lastName,
      country: this.countryCode,
      firstName: form.firstName,
      lastName: form.lastName,
      loginTemp: true,
      phone: form.phone,
      email: form.email,
      userRole: 'temp',
    };
  }*/

  /**
   * Dispara el evento submit del formulario de contacto y valida su estado.
   * - Este método es llamado desde el componente padre.
   */
  submit(): boolean {
    this.contactFormElement.nativeElement.dispatchEvent(
      new Event('submit', { cancelable: true })
    );
    if (!this.contactForm.valid) return false;

    const { documentId, firstName, lastName, phone, email } =
      this.contactForm.getRawValue();

    const formattedGuest = {
      documentId,
      firstName,
      lastName,
      email,
      phone,
      street: '',
      number: '',
      commune: '',
    };
    // Guardar contacto en storage y eliminar dirección seleccionada del storage.
    this.retrieveData.setFormData(this.contactForm.value);
    this.guestStorageService.set(formattedGuest);
    this.checkoutStorageService.removeDeliveryAddress();
    const session = this.sessionStorage.get();
    if (session) {
      const formattedPhone = `${environment.phone.code}${phone}`;
      this.cartApiService
        .updateGuest(session.email, {
          ...formattedGuest,
          phone: formattedPhone,
        })
        .subscribe({
          next: () => {
            // console.log('Contacto actualizado');
          },
          error: (error) => {
            console.error('Error al actualizar contacto', error);
          },
        });
    }

    return this.contactForm.valid;
  }

  /**
   * Dispara el evento submit del formulario de contacto.
   */
  nativeSubmit(): void {
    if (!this.contactForm.valid) this.contactForm.markAllAsTouched();
  }

  /**
   * Abre el modal de inicio de sesión con la configuración predeterminada
   * para permitir que el usuario se autentique.
   */
  openLoginModal(): void {
    const ref = this.dialogService.open(LoginModalComponent, {
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      appendTo: 'body',
      data: {
        email: this.contactForm.get('email')?.value,
      },
      templates: {
        header: LoginHeaderModalComponent,
      },
    });
    ref.onClose.subscribe(() => {
      this.contactForm.patchValue({
        email: '',
      });
    });
  }
}
